import React, { Fragment, useEffect } from 'react'
import styled from 'styled-components'
import { Crown, Ticket, Kingdom } from 'app/assets/icons'

import { fonts, H4, H3, SmallText } from 'app/common/typography'
import type { IntlShape } from 'react-intl'
import background_card_desktop from 'shared/assets/images/kingdom-insert/encart-promo-background-desktop.jpg'
import background_card_app from 'shared/assets/images/kingdom-insert/encart-promo-background-app.jpg'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Link } from 'app/common/link'
import { breakpoints, media, style } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { isMobile } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPointsPageData } from 'shared/store/kingdom/points'
import { featureCodes } from 'shared/utils'
import type { RootState } from 'shared/store/reducers'
import { SPACING } from 'app-v2/design-tokens'

const { chalk, greyBlue, veryDarkGrey, yellow, bbqBrown, mayoEggWhite } = colors

type Props = {
    authenticated?: boolean,
    nbPoints?: number,
    nbCoupons?: number,
    intl: IntlShape
}

const KingdomCard = (props: Props) => {
    const { authenticated, intl, nbPoints, nbCoupons } = props
    const dispatch = useDispatch()
    const signinRoute = intl.formatMessage({
        id: 'routes.clickandcollect.signin.signup'
    })
    const promotionsRoute = intl.formatMessage({
        id: 'routes.clickandcollect.promotions'
    })
    const buttonUrl = authenticated ? promotionsRoute : signinRoute
    const buttonText = authenticated
        ? 'clickandcollect.catalog.kingdom.advantages'
        : 'clickandcollect.catalog.kingdom.connection'

    const isMobile = window.innerWidth < breakpoints.medium

    const availableFeatures = useSelector(
        (state: RootState) => state.featuresState.available
    )
    const displayLotteryWheelBackground = availableFeatures.includes(
        featureCodes.LOTTERY_WHEEL_INSERT_KINGDOM_BACKGROUND_IMAGE
    )

    useEffect(() => {
        if (authenticated) {
            dispatch(fetchPointsPageData())
        }
    }, [authenticated, dispatch])

    return (
        <KingdomPush
            isMobile={isMobile}
            authenticated={authenticated}
            hasImageBackground={displayLotteryWheelBackground}>
            <Left>
                {!authenticated ? (
                    <Header>
                        <Kingdom size={isMobile ? 35 : 50} />
                        <KingdomText>
                            <FormattedMessage id="clickandcollect.catalog.kingdom">
                                {(text: string) => (
                                    <StyledH4 color={mayoEggWhite}>
                                        {text}
                                    </StyledH4>
                                )}
                            </FormattedMessage>
                            <FormattedMessage id="clickandcollect.catalog.kingdom.connect">
                                {(text: string) => (
                                    <StyledText
                                        color={mayoEggWhite}
                                        font={fonts.flameSansRegular}>
                                        {text}
                                    </StyledText>
                                )}
                            </FormattedMessage>
                        </KingdomText>
                    </Header>
                ) : (
                    <Fragment>
                        <TextIcon>
                            <StyledH3 color="white">{nbPoints}</StyledH3>
                            <Crown color={yellow} size={isMobile ? 22 : 35} />
                        </TextIcon>
                        <TextIcon>
                            <StyledH3 color="white">{nbCoupons}</StyledH3>
                            <Ticket color={yellow} size={isMobile ? 22 : 35} />
                        </TextIcon>
                    </Fragment>
                )}
            </Left>
            <Right>
                <StyledLink
                    color="white"
                    background={chalk}
                    url={buttonUrl}
                    isMobile={isMobile}
                    params={{
                        fromUrl: intl.formatMessage({
                            id: 'routes.clickandcollect.catalog'
                        })
                    }}>
                    <FormattedMessage id={buttonText} />
                </StyledLink>
            </Right>
        </KingdomPush>
    )
}

const KingdomPush = styled.div`
    border-radius: ${props => (props.isMobile ? '0' : '0 0 20px 20px')};
    background-size: auto;
    display: flex;
    flex-direction: row;
    justify-content: ${props =>
        props.hasImageBackground ? 'flex-end' : 'space-between'};
    align-items: center;
    padding: 10px 20px;
    ${props =>
        props.hasImageBackground
            ? `background-image: url(${
                  props.isMobile ? background_card_app : background_card_desktop
              });
              background-size: cover;
              `
            : `background: ${bbqBrown};`}
    ${props =>
        props.hasImageBackground &&
        props.isMobile &&
        `margin: 0 -${SPACING.M}`};
`

const Left = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const Right = styled.div``

const Header = styled.div`
    display: flex;
    align-items: center;
`

const KingdomText = styled.div`
    margin-left: 20px;
    padding-top: 5px;
`

const StyledText = styled(SmallText)`
    font-size: 14px;
`

const StyledH4 = styled(H4)`
    line-height: 20px;
`

const StyledLink = styled(Link)`
    ${isMobile ? style.buttonSmall : style.buttonMedium};
    border: none;
    margin-left: 10px;
    background-color: ${yellow};
`

const TextIcon = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: ${isMobile ? '10px 5px' : '10px'};
`

const StyledH3 = styled(H3)`
    margin-right: 5px;
    ${isMobile && 'font-size: 18px;'}
`

const KingdomCardComponent = injectIntl(KingdomCard)
export { KingdomCardComponent }
